import { BadRequestError } from "@/wab/shared/ApiErrors/errors";
import type { CmsFieldMeta } from "@/wab/shared/ApiSchema";
import { cmsRichTextHtmlValid } from "@/wab/shared/copilot/assistant-cms-rich-text";
import { assistantCallSchema } from "@/wab/shared/copilot/assistant-protocol";
import { z } from "zod";

const id = z.string().min(1).max(500);
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const DANGEROUS_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const key = z
  .string()
  .max(500)
  .refine((k) => !DANGEROUS_KEYS.has(k), "Dangerous dictionary key");
const keyNonEmpty = z
  .string()
  .min(1)
  .max(500)
  .refine((k) => !DANGEROUS_KEYS.has(k), "Dangerous dictionary key");

export const ASSISTANT_CMS_DRAFT_BOUNDS = {
  draftUtf8Bytes: 32_000,
  canonicalJournalUtf8Bytes: 131_072,
  localeEntries: 20,
  fieldsPerLocale: 100,
  scalarStringCharacters: 10_000,
  prepareLeaseMilliseconds: 60_000,
  serverLivePreparations: 128,
  listPage: 16,
  nestedDepth: 8,
  nestedItems: 100,
  nestedKeys: 100,
  // Ref scan of the final row (defaults included): characters+nodes walked,
  // distinct targets, distinct target tables, ids per lookup query.
  refWalkWork: 200_000,
  refTargets: 1_000,
  refTables: 50,
  refQueryChunk: 200,
} as const;

const scalar = z.union([
  z.string().max(ASSISTANT_CMS_DRAFT_BOUNDS.scalarStringCharacters),
  z.number().finite(),
  z.boolean(),
  z.null(),
]);

const isPlainObject = (v: unknown): v is Record<string, unknown> => {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
};
/**
 * Structural bound on a nested list/object value: plain JSON only, finite
 * numbers, bounded depth/width, no prototype-bearing keys. Typing against the
 * native field schema happens in `cmsDraftValueMatches` on the server.
 */
function isBoundedNestedJson(v: unknown, depth: number): boolean {
  if (v === null || typeof v === "boolean") return true;
  if (typeof v === "number") return Number.isFinite(v);
  if (typeof v === "string")
    return v.length <= ASSISTANT_CMS_DRAFT_BOUNDS.scalarStringCharacters;
  if (depth >= ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth) return false;
  if (Array.isArray(v))
    return (
      v.length <= ASSISTANT_CMS_DRAFT_BOUNDS.nestedItems &&
      v.every((x) => isBoundedNestedJson(x, depth + 1))
    );
  if (!isPlainObject(v)) return false;
  const keys = Object.keys(v);
  return (
    keys.length <= ASSISTANT_CMS_DRAFT_BOUNDS.nestedKeys &&
    keys.every(
      (k) =>
        k.length > 0 &&
        k.length <= 500 &&
        !DANGEROUS_KEYS.has(k) &&
        isBoundedNestedJson(v[k], depth + 1),
    )
  );
}
const nestedValue = z.custom<unknown[] | Record<string, unknown>>(
  (v) =>
    (Array.isArray(v) || isPlainObject(v)) && isBoundedNestedJson(v, 0),
  "Invalid or unbounded nested list/object value",
);
/** One locale field value: scalar or bounded nested list/object JSON (request and owned read-back). */
const draftFieldValue = z.union([scalar, nestedValue]);
const localeFields = z
  .record(keyNonEmpty, draftFieldValue)
  .refine(
    (fields) =>
      Object.keys(fields).length <= ASSISTANT_CMS_DRAFT_BOUNDS.fieldsPerLocale,
    "Too many fields in locale",
  );
/** Default locale "" is required; other locales require localized fields (checked against the native schema). */
export const assistantCmsDraftDataSchema = z
  .record(key, localeFields)
  .refine((data) => "" in data, "Default locale is required")
  .refine(
    (data) =>
      Object.keys(data).length <= ASSISTANT_CMS_DRAFT_BOUNDS.localeEntries,
    "Too many locale entries",
  )
  .refine(
    (data) =>
      utf8Bytes(JSON.stringify(data)) <=
      ASSISTANT_CMS_DRAFT_BOUNDS.draftUtf8Bytes,
    "Draft payload exceeds byte bound",
  );

export const assistantCmsDraftRequestSchema = z.discriminatedUnion(
  "operation",
  [
    z
      .object({
        operation: z.literal("create"),
        databaseId: id,
        tableId: id,
        schemaDigest: digest,
        identifier: z.string().min(1).max(200),
        draftData: assistantCmsDraftDataSchema,
      })
      .strict(),
    z
      .object({
        operation: z.literal("update"),
        databaseId: id,
        tableId: id,
        schemaDigest: digest,
        rowId: id,
        revision: z.number().int().nonnegative(),
        draftData: assistantCmsDraftDataSchema,
      })
      .strict(),
  ],
);
export type AssistantCmsDraftRequest = z.infer<
  typeof assistantCmsDraftRequestSchema
>;

/** Request plus client-generated effect identity (audit identity, not authority). */
export const assistantCmsDraftEffectRequestSchema = z
  .object({ effectId: id, request: assistantCmsDraftRequestSchema })
  .strict();
export const assistantCmsDraftEffectRefSchema = z
  .object({ effectId: id })
  .strict();
export const assistantCmsDraftListInputSchema = z
  .object({
    cursor: id.nullable().default(null),
    limit: z
      .number()
      .int()
      .min(1)
      .max(ASSISTANT_CMS_DRAFT_BOUNDS.listPage)
      .default(ASSISTANT_CMS_DRAFT_BOUNDS.listPage),
  })
  .strict();
export const assistantCmsOwnedDraftReadInputSchema = z
  .object({
    databaseId: id,
    tableId: id,
    rowId: id,
    fields: z.array(keyNonEmpty).min(1).max(100),
    locales: z.array(key).min(1).max(20),
  })
  .strict();

export const assistantCmsDraftTransportSchema = z
  .object({
    effectId: id,
    request: assistantCmsDraftRequestSchema,
    call: assistantCallSchema,
  })
  .strict();
export const assistantCmsDraftExecuteTransportSchema = z
  .object({ effectId: id, call: assistantCallSchema })
  .strict();

export const assistantCmsDraftReceiptSchema = z
  .object({
    receiptVersion: z.literal(1),
    effectId: id,
    kind: z.enum(["cms_draft_create", "cms_draft_update"]),
    projectId: id,
    workspaceId: id,
    databaseId: id,
    tableId: id,
    rowId: id,
    operation: z.enum(["create", "update"]),
    beforeRevision: z.number().int().nonnegative().nullable(),
    afterRevision: z.number().int().nonnegative(),
    published: z.literal(false),
    scalarDigest: digest,
  })
  .strict();
export type AssistantCmsDraftReceipt = z.infer<
  typeof assistantCmsDraftReceiptSchema
>;

export const assistantCmsDraftDurableReadSchema = z
  .object({
    projectId: id,
    effectId: id,
    requestDigest: digest.nullable(),
    state: z.enum(["prepared", "applied", "cancelled", "expired", "unknown"]),
    outcome: z.enum(["applied", "not_applied", "outcome_unknown"]),
    expiresAt: z.string().datetime().nullable(),
    receipt: assistantCmsDraftReceiptSchema.nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const applied = value.state === "applied";
    const negative = value.state === "cancelled" || value.state === "expired";
    if (
      value.outcome !==
        (applied ? "applied" : negative ? "not_applied" : "outcome_unknown") ||
      applied !== (value.receipt !== null) ||
      (value.state === "unknown") !== (value.expiresAt === null) ||
      (value.state === "unknown") !== (value.requestDigest === null)
    )
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Inconsistent durable CMS effect outcome",
      });
  });

export const assistantCmsDraftJournalEntrySchema = z
  .object({
    request: assistantCmsDraftRequestSchema,
    prepareCall: assistantCallSchema.nullable(),
    executeCall: assistantCallSchema.nullable(),
    effect: assistantCmsDraftDurableReadSchema,
  })
  .strict();
export const assistantCmsDraftListOutputSchema = z
  .object({
    projectId: id,
    actorUserId: id,
    effects: z
      .array(assistantCmsDraftJournalEntrySchema)
      .max(ASSISTANT_CMS_DRAFT_BOUNDS.listPage),
    nextCursor: id.nullable(),
  })
  .strict();
export const assistantCmsOwnedDraftReadOutputSchema = z
  .object({
    projectId: id,
    databaseId: id,
    tableId: id,
    rowId: id,
    revision: z.number().int().nonnegative(),
    published: z.literal(false),
    sourceDigest: digest,
    values: z.record(key, z.record(key, draftFieldValue)),
  })
  .strict();

/** Recursively sorted keys; array order preserved. JSONB roundtrips reorder object keys. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object")
    return `{${Object.keys(value)
      .sort()
      .map(
        (k) =>
          `${JSON.stringify(k)}:${canonicalJson((value as Record<string, unknown>)[k])}`,
      )
      .join(",")}}`;
  return JSON.stringify(value);
}
/** Browser-safe byte length; hashing lives in the server journal module. */
export const utf8Bytes = (s: string) => new TextEncoder().encode(s).length;

export function cmsDraftKind(request: AssistantCmsDraftRequest) {
  return request.operation === "create"
    ? ("cms_draft_create" as const)
    : ("cms_draft_update" as const);
}

/** Digest binds authoritative actor + route project + kind + request + original context. */
export function cmsDraftCanonicalRequest(
  projectId: string,
  actorUserId: string,
  effectId: string,
  rawRequest: unknown,
  originalContext: unknown,
) {
  const request = assistantCmsDraftRequestSchema.parse(rawRequest);
  const canonical = canonicalJson({
    schemaVersion: 1,
    kind: cmsDraftKind(request),
    projectId,
    actorUserId,
    effectId,
    request,
    originalContext,
  });
  if (utf8Bytes(canonical) > ASSISTANT_CMS_DRAFT_BOUNDS.canonicalJournalUtf8Bytes)
    throw new Error("CMS effect request exceeds the durable byte bound");
  return { request, canonical };
}

/** Client-reported audit identity only; never permission or issuance proof. */
export function cmsDraftAuditCall(
  projectId: string,
  effectId: string,
  request: AssistantCmsDraftRequest,
  operation: "prepare" | "execute",
  raw: unknown,
) {
  const call = assistantCallSchema.parse(raw);
  const tool =
    operation === "prepare"
      ? "prepare_cms_draft_write"
      : "execute_prepared_cms_draft_write";
  const ok =
    operation === "prepare"
      ? canonicalJson(
          assistantCmsDraftRequestSchema.parse(call.arguments),
        ) === canonicalJson(request)
      : canonicalJson(call.arguments) ===
        canonicalJson({ preparationId: effectId });
  if (
    call.toolName !== tool ||
    call.expectedContext.projectId !== projectId ||
    !ok ||
    utf8Bytes(JSON.stringify(call)) > 65_536
  )
    throw new Error("CMS effect call binding mismatch");
  return call;
}

/**
 * Native text bounds (`minChars`/`maxChars`) are enforced only by the Studio
 * form (antd length rules: UTF-16 length, skipped for empty optional values).
 * The scalar writer bypasses that form, so it applies the same rule.
 */
export function cmsTextWithinBounds(
  field: { required?: boolean; minChars?: number; maxChars?: number },
  value: string,
) {
  if (value === "" && !field.required) return true;
  return (
    (field.minChars === undefined || value.length >= field.minChars) &&
    (field.maxChars === undefined || value.length <= field.maxChars)
  );
}

const hasOwn = (o: object, k: string) =>
  Object.prototype.hasOwnProperty.call(o, k);
export const CMS_DRAFT_SCALAR_TYPES: readonly string[] = [
  "text",
  "long-text",
  "number",
  "boolean",
  "enum",
  "date-time",
  "color",
  "ref",
  "image",
  "file",
  "rich-text",
];

/**
 * Native date-time storage (`StringDateTimePicker` in CmsInputs.tsx): the form
 * stores `date.toDate()` with milliseconds zeroed as `toISOString()`, i.e. an
 * absolute UTC instant `YYYY-MM-DDTHH:mm:ss.000Z`. A value with an offset,
 * date-only text, fractional seconds or an impossible date is not something
 * the editor can produce, and normalizing it would change the authored value.
 */
const CMS_DATE_TIME_SHAPE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.000Z$/;
export function cmsDateTimeValid(value: string) {
  if (!CMS_DATE_TIME_SHAPE.test(value)) return false;
  const time = Date.parse(value);
  return Number.isFinite(time) && new Date(time).toISOString() === value;
}

/**
 * Native color storage (`CmsColorInput`): Pickr's `toHEXA().toString()`, which
 * is `#` plus upper-case hex digits, RRGGBB with an AA suffix when the alpha
 * channel is not 1 (pickr@1.9.1 `HSVaColor.toHEXA`). Names, rgb()/hsl(), short
 * hex and lower-case hex are inputs Pickr parses but never stores.
 */
const CMS_COLOR_SHAPE = /^#[0-9A-F]{6}(?:[0-9A-F]{2})?$/;
export const cmsColorValid = (value: string) => CMS_COLOR_SHAPE.test(value);

/**
 * Native image/file storage: `cmsFileUpload` (server/routes/cmse.ts) returns a
 * `CmsUploadedFile` `{name, url, mimetype, size, imageMeta?}` and the editor
 * stores that object as is. The upload is content-addressed in one shared
 * bucket and nothing records which project or user owns it, so the writer has
 * no ownership proof for a new URL and never accepts one by itself: a media value is
 * valid only when it has exactly the native shape AND is already stored in the
 * same field of the owned row or in the field's schema default (see
 * `CmsMediaProof`), or carries the `mediaId` of an upload record the server
 * verified for this actor, project and database (see `cmsMediaAssertKnown`).
 * `conformsToType` additionally needs `imageMeta` for images.
 */
const MEDIA_KEYS = new Set(["name", "url", "mimetype", "size", "imageMeta"]);
/**
 * Transport-only id of a server-issued upload record (see `assistant-cms-media.ts`). It rides on a
 * request-side media leaf next to the five native keys and is stripped before anything is stored, so a stored
 * row, a default and the owned read-back never contain it.
 */
export const CMS_MEDIA_ID_PATTERN = /^cmsmedia_[0-9a-f]{32}$/;
export type CmsMediaValueOptions = { allowMediaId?: boolean };
const finiteNonNegative = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) && v >= 0;
/** Exact own-key count without materializing the keys of an arbitrarily wide object. */
function ownKeyCountIs(o: object, n: number) {
  let c = 0;
  for (const k in o) if (Object.prototype.hasOwnProperty.call(o, k) && ++c > n) return false;
  return c === n;
}
/**
 * Native `CmsUploadedFile` shape; `kind` adds the image `imageMeta` requirement. With `allowMediaId` (request
 * side only) one well-formed `mediaId` may accompany the native keys; stored values are always checked without it.
 */
export function cmsUploadedFileValid(
  value: unknown,
  kind: "image" | "file" | "any",
  opts?: CmsMediaValueOptions,
) {
  if (!isPlainObject(value)) return false;
  for (const k in value)
    if (!MEDIA_KEYS.has(k) && !(opts?.allowMediaId && k === "mediaId")) return false;
  if (
    hasOwn(value, "mediaId") &&
    !(typeof value.mediaId === "string" && CMS_MEDIA_ID_PATTERN.test(value.mediaId))
  )
    return false;
  const { name, url, mimetype, size, imageMeta } = value;
  if (
    typeof name !== "string" ||
    typeof url !== "string" ||
    !url ||
    typeof mimetype !== "string" ||
    !mimetype ||
    !finiteNonNegative(size)
  )
    return false;
  if (imageMeta === undefined) return kind !== "image";
  return (
    isPlainObject(imageMeta) &&
    ownKeyCountIs(imageMeta, 2) &&
    finiteNonNegative(imageMeta.width) &&
    finiteNonNegative(imageMeta.height)
  );
}

/**
 * Native rich text is the TinyMCE editor's HTML string; the server stores it
 * verbatim and published content renders it, so this is the only gate. The
 * grammar (links, styles, anchors, tables, codesample, formatting; no media,
 * scripts, event handlers, unsafe URIs or unowned asset URLs) and its parser
 * live in assistant-cms-rich-text.ts; the value is accepted or refused as
 * authored, never rewritten.
 */
export function cmsRichTextValid(value: string) {
  return cmsRichTextHtmlValid(value, ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth);
}

/**
 * True when the whole schema subtree of a field is writable by the draft
 * writer: only scalar/date-time/color/ref/list/object descendants (a ref needs
 * its target table id), unique sibling
 * identifiers, bounded depth, and no hidden/unique/localized descendants
 * (the native editor localizes only root fields). A media/rich-text
 * descendant makes the entire field unsupported: a list write
 * replaces the array wholesale, so it must never silently drop such content.
 */
export function cmsNestedFieldSupported(field: CmsFieldMeta, depth = 0): boolean {
  if (field.type === "ref")
    return typeof field.tableId === "string" && field.tableId.length > 0;
  if (field.type !== "list" && field.type !== "object")
    return CMS_DRAFT_SCALAR_TYPES.includes(field.type);
  if (
    depth >= ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth ||
    !Array.isArray(field.fields)
  )
    return false;
  const seen = new Set<string>();
  return field.fields.every((child) => {
    if (
      !child ||
      typeof child.identifier !== "string" ||
      !child.identifier ||
      DANGEROUS_KEYS.has(child.identifier) ||
      seen.has(child.identifier) ||
      child.hidden ||
      child.unique ||
      child.localized
    )
      return false;
    seen.add(child.identifier);
    return cmsNestedFieldSupported(child, depth + 1);
  });
}

/**
 * Typed validation of one draft value against its native field. Null clears
 * an optional field. Objects accept only the schema's own identifiers and
 * require every `required` descendant to be present and non-null (the native
 * form's required rule); list items are objects of the list's fields.
 */
export function cmsDraftValueMatches(
  field: CmsFieldMeta,
  value: unknown,
  opts?: CmsMediaValueOptions,
): boolean {
  if (value === null) return !field.required;
  switch (field.type) {
    case "text":
    case "long-text":
      return typeof value === "string" && cmsTextWithinBounds(field, value);
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "boolean":
      return typeof value === "boolean";
    case "enum":
      return typeof value === "string" && field.options.includes(value);
    case "date-time":
      return typeof value === "string" && cmsDateTimeValid(value);
    case "color":
      return typeof value === "string" && cmsColorValid(value);
    case "ref":
      return (
        typeof value === "string" && value.length > 0 && value.length <= 500
      );
    case "image":
    case "file":
      return cmsUploadedFileValid(value, field.type, opts);
    case "rich-text":
      // The editor's required rule rejects "", then antd length rules apply.
      return (
        typeof value === "string" &&
        (value !== "" || !field.required) &&
        cmsTextWithinBounds(field, value) &&
        cmsRichTextValid(value)
      );
    case "list":
      return (
        Array.isArray(value) &&
        value.every((item) => objectMatches(field.fields, item, opts))
      );
    case "object":
      return objectMatches(field.fields, value, opts);
    default:
      return false;
  }
}
function objectMatches(
  fields: CmsFieldMeta[],
  value: unknown,
  opts?: CmsMediaValueOptions,
): boolean {
  if (!isPlainObject(value)) return false;
  const keys = Object.keys(value);
  if (!keys.every((k) => fields.some((f) => f.identifier === k))) return false;
  return fields.every((f) => {
    if (!hasOwn(value, f.identifier) || value[f.identifier] === undefined)
      return !f.required;
    return cmsDraftValueMatches(f, value[f.identifier], opts);
  });
}
const REF_BOUNDS_MESSAGE = "CMS ref scan exceeds the supported schema, default or target bounds";
/**
 * Own enumerable keys, visited lazily: stops (returns false) as soon as `limit`
 * keys were exceeded, so nothing larger than limit+1 keys is ever visited or
 * materialized. for-in also reaches inherited keys, hence the own-property guard.
 */
function forEachOwnKeyBounded(
  o: object,
  limit: number,
  visit: (k: string) => void,
): boolean {
  let n = 0;
  for (const k in o) {
    if (!Object.prototype.hasOwnProperty.call(o, k)) continue;
    if (++n > limit) return false;
    visit(k);
  }
  return true;
}
const REF_MALFORMED_MESSAGE =
  "CMS create would store a malformed ref value or a ref field without a target table";

/**
 * Bounded, deduplicating collector of ref targets. The walk spends one unit per
 * schema field/value node plus the characters of every string, and refuses
 * (rather than truncating) past the bounds, so an oversized schema or default
 * fails closed before anything is materialized or queried.
 */
export class CmsRefScan {
  readonly byTable = new Map<string, Set<string>>();
  private targets = 0;
  private work = 0;
  spend(units = 1) {
    this.work += units;
    if (this.work > ASSISTANT_CMS_DRAFT_BOUNDS.refWalkWork)
      throw new BadRequestError(REF_BOUNDS_MESSAGE);
  }
  add(tableId: string, rowId: string) {
    let ids = this.byTable.get(tableId);
    if (!ids) {
      if (this.byTable.size >= ASSISTANT_CMS_DRAFT_BOUNDS.refTables)
        throw new BadRequestError(REF_BOUNDS_MESSAGE);
      this.byTable.set(tableId, (ids = new Set()));
    }
    if (ids.has(rowId)) return;
    if (++this.targets > ASSISTANT_CMS_DRAFT_BOUNDS.refTargets)
      throw new BadRequestError(REF_BOUNDS_MESSAGE);
    ids.add(rowId);
  }
}

/**
 * Work-bounded walk of a table's raw defaults for the given locales, run before
 * the native create merge clones them: the schema width and the default JSON
 * size/depth must fit, whatever the (small) authored request is.
 */
export function cmsAssertCreateDefaultsBounded(
  fields: CmsFieldMeta[],
  locales: Iterable<string>,
  scan: CmsRefScan = new CmsRefScan(),
) {
  if (!Array.isArray(fields)) throw new BadRequestError(REF_BOUNDS_MESSAGE);
  const walk = (v: unknown, depth: number) => {
    scan.spend(1);
    if (typeof v === "string") scan.spend(v.length);
    else if (v && typeof v === "object") {
      if (depth >= ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth)
        throw new BadRequestError(REF_BOUNDS_MESSAGE);
      // Each child spends work, so the budget stops this before the whole object.
      for (const k in v)
        if (Object.prototype.hasOwnProperty.call(v, k))
          walk((v as any)[k], depth + 1);
    }
  };
  const loc = [...locales];
  for (const f of fields) {
    scan.spend(1);
    const map = f?.defaultValueByLocale;
    if (!map || typeof map !== "object") continue;
    for (const l of loc) if (hasOwn(map, l)) walk((map as any)[l], 0);
  }
  return scan;
}

/** True when a (bounded) schema subtree contains a ref field (or is too deep to tell). */
function cmsFieldHasRef(field: CmsFieldMeta, scan: CmsRefScan, depth = 0): boolean {
  scan.spend(1);
  if (field?.type === "ref") return true;
  if (
    (field?.type !== "list" && field?.type !== "object") ||
    !Array.isArray(field.fields)
  )
    return false;
  // Too deep to inspect: assume a ref below, so the value walk refuses it.
  if (depth >= ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth) return true;
  return field.fields.some((c) => cmsFieldHasRef(c, scan, depth + 1));
}

/**
 * Validates every ref value a (merged, possibly unauthored) field value holds
 * and collects its (tableId, rowId) target into `scan`, deduplicating while it
 * walks. A ref value must be a non-empty string <= 500 chars under a ref field
 * that names a target table; list/object containers on the way to a ref must
 * have the right shape and fit the draft item/key/depth bounds. Absent/null
 * values and fields without refs are not judged: a required field the draft
 * leaves incomplete is allowed by native create and stays so.
 */
export function cmsDraftRefTargets(
  field: CmsFieldMeta,
  value: unknown,
  scan: CmsRefScan = new CmsRefScan(),
  depth = 0,
) {
  scan.spend(1);
  if (value === null || value === undefined || !field) return scan;
  if (field.type === "ref") {
    if (
      typeof field.tableId !== "string" ||
      !field.tableId ||
      typeof value !== "string" ||
      !value ||
      value.length > 500
    )
      throw new BadRequestError(REF_MALFORMED_MESSAGE);
    scan.add(field.tableId, value);
  } else if (field.type === "list" || field.type === "object") {
    if (!Array.isArray(field.fields) || !cmsFieldHasRef(field, scan)) return scan;
    if (depth >= ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth)
      throw new BadRequestError(REF_BOUNDS_MESSAGE);
    if (field.type === "list") {
      if (!Array.isArray(value)) throw new BadRequestError(REF_MALFORMED_MESSAGE);
      if (value.length > ASSISTANT_CMS_DRAFT_BOUNDS.nestedItems)
        throw new BadRequestError(REF_BOUNDS_MESSAGE);
      for (const item of value)
        cmsRefTargetsOfObject(field.fields, item, scan, depth + 1);
    } else cmsRefTargetsOfObject(field.fields, value, scan, depth + 1);
  }
  return scan;
}
function cmsRefTargetsOfObject(
  fields: CmsFieldMeta[],
  value: unknown,
  scan: CmsRefScan,
  depth: number,
) {
  if (value === null || value === undefined) return;
  if (!isPlainObject(value)) throw new BadRequestError(REF_MALFORMED_MESSAGE);
  if (!forEachOwnKeyBounded(value, ASSISTANT_CMS_DRAFT_BOUNDS.nestedKeys, () => {}))
    throw new BadRequestError(REF_BOUNDS_MESSAGE);
  scan.spend(fields.length);
  for (const f of fields)
    if (f && typeof f.identifier === "string" && hasOwn(value, f.identifier))
      cmsDraftRefTargets(f, value[f.identifier], scan, depth);
}
/** The whole final row (every locale, every schema field, unauthored defaults included). */
export function cmsRowRefTargets(
  fields: CmsFieldMeta[],
  row: Record<string, unknown>,
  scan: CmsRefScan,
) {
  for (const locale in row) {
    if (!Object.prototype.hasOwnProperty.call(row, locale)) continue;
    scan.spend(1);
    const perLocale = row[locale];
    if (!isPlainObject(perLocale)) continue;
    scan.spend(fields.length);
    for (const f of fields)
      if (hasOwn(perLocale, f.identifier))
        cmsDraftRefTargets(f, perLocale[f.identifier], scan);
  }
  return scan;
}

const MEDIA_BOUNDS_MESSAGE = "CMS media provenance scan exceeds the supported schema or value bounds";
const MEDIA_PROOF_MESSAGE =
  "CMS image or file value is not already stored in this field or its default; new uploads cannot be proven owned";
/**
 * Provenance of media values: the canonical native objects already stored in
 * one root field of the owned row and in that field's schema default for the
 * locale. A write may keep, reorder or copy those inside the same field (no
 * asset access is gained); the only other admission is a leaf with a verified
 * `mediaId`, judged per position outside this set. The walk shares the ref scan's
 * work budget and never trusts the stored shape beyond exact equality.
 */
export class CmsMediaProof {
  readonly known = new Set<string>();
  private work = 0;
  spend(units = 1) {
    this.work += units;
    if (this.work > ASSISTANT_CMS_DRAFT_BOUNDS.refWalkWork)
      throw new BadRequestError(MEDIA_BOUNDS_MESSAGE);
  }
}
type CmsMediaVisit = (leaf: unknown, path: string, fieldType: "image" | "file") => void;
/**
 * Visits every value at an image/file schema position. `path` is the position inside the root field's value
 * (list index or percent-encoded object key per level), so a caller can tell two leaves with equal content apart.
 */
function cmsMediaWalk(
  field: CmsFieldMeta,
  value: unknown,
  visit: CmsMediaVisit,
  proof: CmsMediaProof,
  depth = 0,
  path = "",
) {
  proof.spend(1);
  if (value === null || value === undefined || !field) return;
  if (field.type === "image" || field.type === "file")
    return visit(value, path, field.type);
  if (
    (field.type !== "list" && field.type !== "object") ||
    !Array.isArray(field.fields) ||
    depth >= ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth
  )
    return;
  const each = (item: unknown, itemPath: string) => {
    proof.spend(1 + field.fields.length);
    if (!isPlainObject(item)) return;
    for (const f of field.fields)
      if (f && typeof f.identifier === "string" && hasOwn(item, f.identifier))
        cmsMediaWalk(
          f,
          item[f.identifier],
          visit,
          proof,
          depth + 1,
          `${itemPath}/${encodeURIComponent(f.identifier)}`,
        );
  };
  if (field.type === "list") {
    if (Array.isArray(value)) value.forEach((item, i) => each(item, `${path}/${i}`));
  } else each(value, path);
}
/**
 * True when a field's schema subtree holds an image or file field. The scan
 * spends the shared media work budget (nodes and children, before descending
 * into them), so an overbroad or deep schema fails closed instead of scanning.
 */
export function cmsFieldHasMedia(
  field: CmsFieldMeta,
  budget: CmsMediaProof = new CmsMediaProof(),
  depth = 0,
): boolean {
  budget.spend(1);
  if (field?.type === "image" || field?.type === "file") return true;
  if (
    (field?.type !== "list" && field?.type !== "object") ||
    !Array.isArray(field.fields) ||
    depth >= ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth
  )
    return false;
  budget.spend(field.fields.length);
  for (const c of field.fields) if (cmsFieldHasMedia(c, budget, depth + 1)) return true;
  return false;
}
/**
 * Canonical text of a native media leaf, or undefined when it is not native
 * shaped. Stored (historical) rows and defaults never passed these bounds, so
 * the string lengths are charged to the shared budget and held to the scalar
 * string bound before anything is serialized; over-bound data fails closed.
 * Characters are charged at one work unit per `MEDIA_CHARS_PER_WORK_UNIT`: the
 * established `refWalkWork` budget is kept as is, and a unit per character
 * would refuse the ordinary 5000-item native gallery it already admits.
 */
const MEDIA_CHARS_PER_WORK_UNIT = 8;
function cmsMediaLeafKey(leaf: unknown, proof: CmsMediaProof) {
  if (!cmsUploadedFileValid(leaf, "any")) return undefined;
  const { name, url, mimetype } = leaf as Record<string, string>;
  for (const str of [name, url, mimetype]) {
    if (str.length > ASSISTANT_CMS_DRAFT_BOUNDS.scalarStringCharacters)
      throw new BadRequestError(MEDIA_BOUNDS_MESSAGE);
    proof.spend(Math.ceil(str.length / MEDIA_CHARS_PER_WORK_UNIT));
  }
  return canonicalJson(leaf);
}
/** Adds the native media objects found under `value` (at its schema positions) to the proof. */
export function cmsMediaCollect(field: CmsFieldMeta, value: unknown, proof: CmsMediaProof) {
  cmsMediaWalk(
    field,
    value,
    (leaf) => {
      const key = cmsMediaLeafKey(leaf, proof);
      if (key !== undefined) proof.known.add(key);
    },
    proof,
  );
}
/**
 * Refuses every media object of `value` that the proof does not already hold. `owned` maps a position
 * (see `cmsMediaWalk`) to the canonical metadata the server verified for the `mediaId` at that position: such a
 * position is judged by that exact metadata alone, whether or not the id is still attached (the merged create
 * value has no id), and every other position by the row/default proof alone. Verified metadata never joins
 * `proof.known`, so an identical untagged leaf elsewhere cannot borrow it.
 */
export function cmsMediaAssertKnown(
  field: CmsFieldMeta,
  value: unknown,
  proof: CmsMediaProof,
  owned?: ReadonlyMap<string, string>,
) {
  const budget = new CmsMediaProof();
  cmsMediaWalk(
    field,
    value,
    (leaf, path) => {
      const verified = owned?.get(path);
      if (verified !== undefined) {
        const bare = isPlainObject(leaf) && hasOwn(leaf, "mediaId") ? omitMediaId(leaf) : leaf;
        if (cmsMediaLeafKey(bare, budget) !== verified)
          throw new BadRequestError(MEDIA_PROOF_MESSAGE);
        return;
      }
      const key = cmsMediaLeafKey(leaf, budget);
      if (key === undefined || !proof.known.has(key))
        throw new BadRequestError(MEDIA_PROOF_MESSAGE);
    },
    budget,
  );
}
const omitMediaId = (leaf: Record<string, unknown>) => {
  const { mediaId: _id, ...rest } = leaf;
  return rest;
};

/** An upload-record id found on a request-side media leaf, with the leaf's position and its id-free metadata. */
export type CmsMediaIdLeaf = {
  path: string;
  fieldType: "image" | "file";
  mediaId: string;
  metadata: Record<string, unknown>;
};
/** The id-bearing media leaves of `value` at their schema positions (leaves without an id are skipped). */
export function cmsMediaIdLeaves(
  field: CmsFieldMeta,
  value: unknown,
  budget: CmsMediaProof = new CmsMediaProof(),
): CmsMediaIdLeaf[] {
  const found: CmsMediaIdLeaf[] = [];
  cmsMediaWalk(
    field,
    value,
    (leaf, path, fieldType) => {
      if (!isPlainObject(leaf) || !hasOwn(leaf, "mediaId")) return;
      if (typeof leaf.mediaId !== "string") return;
      found.push({ path, fieldType, mediaId: leaf.mediaId, metadata: omitMediaId(leaf) });
    },
    budget,
  );
  return found;
}

/**
 * Schema-free strip of the transport-only id: every plain object whose only keys are the native media keys plus
 * one well-formed `mediaId` loses that key. The writer proves (by count, in `validateAndLockScope`) that these
 * are exactly the id-bearing leaves at image/file positions, so the journal can recompute the id-free value (and
 * so the receipt digest) from the persisted request alone. Without ids the result is canonically identical.
 */
export function cmsDraftMediaIds(draftData: unknown) {
  const ids = new Set<string>();
  let count = 0;
  const maxDepth = ASSISTANT_CMS_DRAFT_BOUNDS.nestedDepth + 3;
  const walk = (v: unknown, depth: number): unknown => {
    if (depth > maxDepth) throw new BadRequestError(MEDIA_BOUNDS_MESSAGE);
    if (Array.isArray(v)) return v.map((x) => walk(x, depth + 1));
    if (!isPlainObject(v)) return v;
    const mediaShaped =
      typeof v.mediaId === "string" &&
      CMS_MEDIA_ID_PATTERN.test(v.mediaId) &&
      Object.keys(v).every((k) => k === "mediaId" || MEDIA_KEYS.has(k));
    if (mediaShaped) {
      ids.add(v.mediaId as string);
      count++;
    }
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v)) {
      if (mediaShaped && k === "mediaId") continue;
      Object.defineProperty(out, k, {
        value: walk(v[k], depth + 1),
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
    return out;
  };
  const stripped = walk(draftData, 0);
  return { stripped, ids: [...ids].sort(), count };
}
/** True when a stored field value is within the draft value bounds (depth, items, keys, string length). */
export const cmsDraftFieldValueBounded = (value: unknown) =>
  draftFieldValue.safeParse(value).success;

/** Canonical text of the supported authored scalar projection (digested server-side). */
export const cmsScalarCanonical = (draftData: unknown) =>
  canonicalJson(draftData);
